// Modello di navigazione dell'area admin — fonte unica per Sidebar (desktop),
// BottomNav e foglio "Altro" (mobile). Aggiungere una sezione qui la fa
// comparire ovunque, senza mappe duplicate.

import {
  LayoutDashboard,
  Briefcase,
  CheckSquare,
  FileText,
  MessageSquare,
  Settings,
  Activity,
  CreditCard,
  ArrowLeftRight,
  Wallet,
  BarChart3,
  HandCoins,
  NotebookPen,
  BadgePercent,
  CalendarDays,
  FileSignature,
  Handshake,
  Target,
  Presentation,
  Waypoints,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  superAdminOnly?: boolean;
  /** chiave per canAccess() */
  module: string;
  /** etichetta corta per la barra in basso, dove lo spazio è di 64px */
  short?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Operations",
    items: [
      { to: "/admin", label: "Dashboard", short: "Home", icon: LayoutDashboard, end: true, module: "dashboard" },
      { to: "/admin/goals", label: "Obiettivi", icon: Target, module: "goals" },
      { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, module: "agenda" },
      { to: "/admin/tasks", label: "Tasks", icon: CheckSquare, module: "tasks" },
      { to: "/admin/requests", label: "Richieste", icon: MessageSquare, module: "requests" },
      { to: "/admin/activity", label: "Attività", icon: Activity, module: "activity" },
    ],
  },
  {
    label: "Clienti & Progetti",
    items: [
      // Una voce sola: i lavori vivono dentro il cliente. /admin/projects resta
      // raggiungibile (deep link dai preventivi, scheda del singolo lavoro) ma
      // non è più una sezione a sé nel menu.
      { to: "/admin/clients", label: "Clienti & Lavori", short: "Clienti", icon: Briefcase, module: "clients" },
      { to: "/admin/boards", label: "Board", icon: Presentation, module: "boards" },
      { to: "/admin/filo-rosso", label: "Il Filo Rosso", short: "Filo", icon: Waypoints, superAdminOnly: true, module: "filo" },
      { to: "/admin/quotes", label: "Preventivi", icon: FileSignature, module: "quotes" },
      { to: "/admin/referrals", label: "Referenti", icon: Handshake, module: "referrals" },
      { to: "/admin/pricing", label: "Listino prezzi", short: "Listino", icon: BadgePercent, module: "pricing" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/admin/finance", label: "Panoramica", short: "Finanze", icon: Wallet, end: true, module: "finance" },
      { to: "/admin/finance/movements", label: "Movimenti", icon: ArrowLeftRight, module: "finance" },
      { to: "/admin/finance/reports", label: "Report", icon: BarChart3, module: "finance" },
      { to: "/admin/finance/salaries", label: "Stipendi", icon: HandCoins, superAdminOnly: true, module: "salaries" },
      { to: "/admin/subscriptions", label: "Abbonamenti", short: "Abbonam.", icon: CreditCard, superAdminOnly: true, module: "subscriptions" },
    ],
  },
  {
    label: "Personale",
    items: [
      { to: "/admin/notes", label: "Agenda personale", short: "Note", icon: NotebookPen, superAdminOnly: true, module: "notes" },
    ],
  },
  {
    label: "Sistema",
    items: [
      { to: "/admin/reports", label: "Report Clienti", short: "Report", icon: FileText, module: "reports" },
      { to: "/admin/settings", label: "Impostazioni", short: "Setup", icon: Settings, module: "settings" },
    ],
  },
];

/**
 * Le quattro destinazioni della barra in basso, in ordine di priorità: si
 * prende la prima accessibile di ciascuna. Il quinto slot è sempre "Altro".
 */
export const BOTTOM_NAV_PATHS = ["/admin", "/admin/tasks", "/admin/agenda", "/admin/clients"];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

export function findNavItem(path: string): NavItem | undefined {
  return ALL_ITEMS.find((i) => i.to === path);
}

/** Filtra il modello secondo ruoli e moduli abilitati dell'utente corrente. */
export function visibleGroups(
  isSuperAdmin: boolean,
  canAccess: (module: string) => boolean,
): NavGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => {
      if (it.superAdminOnly && !isSuperAdmin) return false;
      return canAccess(it.module);
    }),
  })).filter((g) => g.items.length > 0);
}
